// Bid Tracker report: the sheets behind the Report tab and its Excel export.
//
// GET crm/bids/report returns a month's movement (bids submitted, won, lost and
// no-bid, from bid_status_history), the bids still in flight, ex VAT totals,
// activity by owner, win rates and every status change. This module turns that
// JSON into header rows and data rows. The screen tables and the workbook both
// read the same sheets, so what a person sees on the tab is what they get in
// Excel. It also carries the date-range helpers the tab's shortcuts use.
//
// Pure functions only: nothing here knows about React, the API or the
// spreadsheet library, so tests/bid-report-export.test.mjs can run them.
import { formatValueGbp } from './bidValue.js';

/* ── Formatting ──────────────────────────────────────────────────────────── */

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad2 = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

// A UTC Date from 'YYYY-MM-DD', an ISO datetime or a SQLite 'YYYY-MM-DD
// HH:MM:SS'; null for anything else. The clock time is kept where present.
const TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;
function utcDate(v) {
  if (!v) return null;
  const m = String(v).trim().match(TIMESTAMP);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)));
  return isNaN(d) ? null : d;
}

/** "31 August 2026". Legacy text that is not a date passes through; no value is ''. */
export function fmtDateLong(v) {
  if (v == null || v === '') return '';
  const d = utcDate(v);
  if (!d) return String(v);
  return `${pad2(d.getUTCDate())} ${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "31 August 2026, 09:15", or the date alone when the value has no clock time. */
export function fmtDateTimeLong(v) {
  const date = fmtDateLong(v);
  const d = utcDate(v);
  if (!d || !/[T ]\d{2}:\d{2}/.test(String(v))) return date;
  return `${date}, ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** Whole pounds with thousands grouped: "£1,250,000". Not a number is ''. */
export function fmtMoney(n) {
  const v = typeof n === 'string' && n.trim() !== '' ? Number(n) : n;
  if (v == null || typeof v !== 'number' || !isFinite(v)) return '';
  return '£' + Math.round(v).toLocaleString('en-GB');
}

/** A win-rate fraction as a whole percentage: 0.25 is "25%"; null is "n/a". */
export function fmtPercent(rate) {
  if (rate == null || rate === '' || !isFinite(Number(rate))) return 'n/a';
  return Math.round(Number(rate) * 100) + '%';
}

/** owner_name from the joined person, else the legacy text, else "No owner". */
export const ownerLabel = (b) => (b && (b.owner_name || b.owner)) || 'No owner';

/* ── Date ranges ─────────────────────────────────────────────────────────── */

// First and last day of a month, in UTC like every other date on the tracker.
// Date.UTC carries a month of -1 into December of the year before.
function monthRange(year, monthIndex) {
  return { from: isoDay(new Date(Date.UTC(year, monthIndex, 1))), to: isoDay(new Date(Date.UTC(year, monthIndex + 1, 0))) };
}
/** The report's default range: the previous calendar month. */
export function previousCalendarMonth(today = new Date()) {
  return monthRange(today.getUTCFullYear(), today.getUTCMonth() - 1);
}
/** The "This month" shortcut. */
export function thisMonthRange(today = new Date()) {
  return monthRange(today.getUTCFullYear(), today.getUTCMonth());
}
/** The "Last month" shortcut, which is the default range. */
export const lastMonthRange = previousCalendarMonth;

export function reportFileName(from, to) {
  return `bid-report-${from}-to-${to}.xlsx`;
}

/* ── Sheets ──────────────────────────────────────────────────────────────── */

export const REPORT_SHEET_NAMES = ['Summary', 'Submitted', 'Outcomes', 'In flight', 'By owner', 'Win rate by sector', 'Win rate by service line', 'Status changes'];

const arr = (v) => (Array.isArray(v) ? v : []);
// A number for a money cell, or '' so Excel gets a blank rather than a zero
// that would be summed as if it were a price.
const num = (v) => (v == null || v === '' || !isFinite(Number(v)) ? '' : Number(v));
const text = (v) => (v == null ? '' : String(v));

// How a value was arrived at, so a reader knows what the ex VAT column holds.
function valueBasis(b) {
  if (b.value_gbp == null) return 'Unpriced';
  if (b.value_basis === 'framework_ceiling') return 'Framework ceiling';
  return b.value_vat === 'inc' ? 'Contract value, inc VAT shown ex VAT' : 'Contract value';
}
const ceilingGbp = (b) => (b.value_gbp != null && b.value_basis === 'framework_ceiling' ? num(b.value_gbp) : '');

function summarySheet(r) {
  const won = arr(r.won).length, lost = arr(r.lost).length;
  const v = r.values || {};
  const ceilings = v.framework_ceilings || {};
  const rows = [
    ['Report period', `${fmtDateLong(r.from)} to ${fmtDateLong(r.to)}`],
    ['Bids submitted', arr(r.submitted).length],
    ['Won', won],
    ['Lost', lost],
    ['No-bid', arr(r.no_bid).length],
    ['Still in flight', arr(r.in_flight).length],
    ['Submitted value ex VAT', fmtMoney(v.submitted_ex_vat ?? 0)],
    ['Won value ex VAT', fmtMoney(v.won_ex_vat ?? 0)],
    ['Lost value ex VAT', fmtMoney(v.lost_ex_vat ?? 0)],
    ['In flight value ex VAT', fmtMoney(v.in_flight_ex_vat ?? 0)],
    ['Framework ceilings', `${ceilings.count ?? 0} (${fmtMoney(ceilings.total_gbp ?? 0)})`],
    ['Unpriced bids', v.unpriced ?? 0],
    ['Win rate (won of won and lost)', fmtPercent(won + lost ? won / (won + lost) : null)],
  ];
  return { name: 'Summary', title: 'Summary', headers: ['Measure', 'Value'], rows, money: [], percent: [], ids: [] };
}

function submittedSheet(r) {
  const headers = ['Reference', 'Opportunity', 'Buyer', 'Sector', 'Service line', 'Owner', 'Submitted on', 'Deadline', 'Value ex VAT (£)', 'Framework ceiling (£)', 'Value basis', 'Status now'];
  const bids = arr(r.submitted);
  const rows = bids.map(b => [
    text(b.reference), text(b.opportunity), text(b.buyer), text(b.sector), text(b.service_line), ownerLabel(b),
    fmtDateLong(b.moved_at), b.deadline ? fmtDateTimeLong(b.deadline_time ? `${b.deadline} ${b.deadline_time}` : b.deadline) : 'No deadline',
    num(b.value_ex_vat), ceilingGbp(b), valueBasis(b), text(b.status),
  ]);
  return { name: 'Submitted', title: 'Bids submitted', headers, rows, money: [headers.indexOf('Value ex VAT (£)'), headers.indexOf('Framework ceiling (£)')], percent: [], ids: bids.map(b => b.id) };
}

function outcomesSheet(r) {
  const headers = ['Reference', 'Opportunity', 'Buyer', 'Sector', 'Service line', 'Owner', 'Outcome', 'Result', 'Result date', 'Winning supplier', 'Winning price (£)', 'No-bid reason', 'Value ex VAT (£)', 'Decided on'];
  const buckets = [['Won', arr(r.won)], ['Lost', arr(r.lost)], ['No-bid', arr(r.no_bid)]];
  const rows = [], ids = [];
  for (const [label, bids] of buckets) {
    for (const b of bids) {
      rows.push([
        text(b.reference), text(b.opportunity), text(b.buyer), text(b.sector), text(b.service_line), ownerLabel(b),
        label, text(b.result), fmtDateLong(b.result_date), text(b.winning_supplier), num(b.winning_price_gbp),
        text(b.no_bid_reason), num(b.value_ex_vat), fmtDateLong(b.moved_at),
      ]);
      ids.push(b.id);
    }
  }
  return { name: 'Outcomes', title: 'Outcomes', headers, rows, money: [headers.indexOf('Winning price (£)'), headers.indexOf('Value ex VAT (£)')], percent: [], ids };
}

function inFlightSheet(r) {
  const headers = ['Reference', 'Opportunity', 'Buyer', 'Sector', 'Owner', 'Status', 'Deadline', 'Time', 'Value ex VAT (£)', 'Framework ceiling (£)', 'Value basis'];
  const bids = arr(r.in_flight);
  const rows = bids.map(b => [
    text(b.reference), text(b.opportunity), text(b.buyer), text(b.sector), ownerLabel(b), text(b.status),
    b.deadline ? fmtDateLong(b.deadline) : 'No deadline', b.deadline ? text(b.deadline_time) : '',
    num(b.value_ex_vat), ceilingGbp(b), valueBasis(b),
  ]);
  return { name: 'In flight', title: 'Still in flight', headers, rows, money: [headers.indexOf('Value ex VAT (£)'), headers.indexOf('Framework ceiling (£)')], percent: [], ids: bids.map(b => b.id) };
}

function byOwnerSheet(r) {
  const headers = ['Owner', 'Submitted', 'Won', 'Lost', 'No-bid', 'In flight', 'Status changes'];
  const rows = arr(r.by_owner).map(o => [o.owner_name || 'No owner', o.submitted ?? 0, o.won ?? 0, o.lost ?? 0, o.no_bid ?? 0, o.in_flight ?? 0, o.changes ?? 0]);
  return { name: 'By owner', title: 'Activity by owner', headers, rows, money: [], percent: [], ids: [] };
}

function winRateSheet(name, keyLabel, entries) {
  const headers = [keyLabel, 'Won', 'Lost', 'Win rate'];
  const rows = arr(entries).map(e => [text(e.key) || 'Not set', e.won ?? 0, e.lost ?? 0, e.rate == null ? 'n/a' : Number(e.rate)]);
  return { name, title: name, headers, rows, money: [], percent: [3], ids: [] };
}

function changesSheet(r) {
  const headers = ['When', 'Reference', 'Opportunity', 'From', 'To', 'Changed by'];
  const changes = arr(r.changes);
  const rows = changes.map(c => [fmtDateTimeLong(c.changed_at), text(c.reference), text(c.opportunity), c.from_status || 'Created', text(c.to_status), text(c.changed_by_name)]);
  return { name: 'Status changes', title: 'Status changes', headers, rows, money: [], percent: [], ids: changes.map(c => c.bid_id) };
}

/**
 * The report as sheets, in REPORT_SHEET_NAMES order. Each is
 * `{ name, title, headers, rows, money, percent, ids }`: `money` and `percent`
 * are the column indexes the writer formats as pounds and as a percentage (the
 * cells hold plain numbers so Excel can total them; a null rate is the text
 * "n/a"), and `ids` is the bid id behind each row where there is one, so the
 * screen can link the row to the bid.
 */
export function buildReportSheets(report) {
  const r = report || {};
  return [
    summarySheet(r),
    submittedSheet(r),
    outcomesSheet(r),
    inFlightSheet(r),
    byOwnerSheet(r),
    winRateSheet('Win rate by sector', 'Sector', r.win_rate && r.win_rate.by_sector),
    winRateSheet('Win rate by service line', 'Service line', r.win_rate && r.win_rate.by_service_line),
    changesSheet(r),
  ];
}

// Re-exported so the Report tab's headline tiles round the same way the rest
// of the tracker does ("£1.3m"), while the tables use fmtMoney in full.
export { formatValueGbp };
